import { serverTimestamp } from 'firebase/firestore';
import { LOG_TYPES } from '../../constants/logTypeRegistry';

export const buildClearedTimelineDetails = () => ({
  'metadata.careDetails': [],
  'metadata.storyBuckets': [],
  storyBuckets: [],
  careDetails: [],
  interventions: [],
  supportStrategies: [],
});

const getSuggestedRecordText = (suggestedRecord = {}, fallbackText = '') => String(
  suggestedRecord.record?.text
  || suggestedRecord.record?.summary
  || suggestedRecord.sourceText
  || fallbackText
  || ''
).trim();

const getSuggestedRecordCategory = (suggestedRecord = {}, fallbackCategory = 'log') => {
  const category = String(
    suggestedRecord.record?.category
    || suggestedRecord.record?.categoryId
    || suggestedRecord.record?.type
    || suggestedRecord.candidateType
    || fallbackCategory
    || 'log'
  ).trim();

  return LOG_TYPES[category] ? category : fallbackCategory || 'log';
};

const getSuggestedRecordMetadata = (suggestedRecord = {}) => (
  suggestedRecord.record?.metadata && typeof suggestedRecord.record.metadata === 'object'
    ? suggestedRecord.record.metadata
    : suggestedRecord.metadata && typeof suggestedRecord.metadata === 'object'
      ? suggestedRecord.metadata
      : {}
);

const getSuggestedRecordStoryBuckets = (suggestedRecord = {}) => {
  const metadata = getSuggestedRecordMetadata(suggestedRecord);
  return Array.isArray(metadata.storyBuckets) ? metadata.storyBuckets : [];
};

const getSuggestedRecordRawHints = (suggestedRecord = {}) => {
  const metadata = getSuggestedRecordMetadata(suggestedRecord);
  return Array.isArray(metadata.rawHints) ? metadata.rawHints : [];
};

export const buildTimelineRefreshPayload = ({
  updatedEntry,
  extractionResult,
  category,
  updatedBy = null,
}) => {
  const suggestedRecords = Array.isArray(extractionResult?.suggestedRecords)
    ? extractionResult.suggestedRecords
    : [];
  const primaryRecord = suggestedRecords[0] || {};
  const record = primaryRecord.record || {};
  const refreshedCategory = getSuggestedRecordCategory(primaryRecord, category);
  const refreshedText = getSuggestedRecordText(primaryRecord, updatedEntry.text);
  const storyBuckets = getSuggestedRecordStoryBuckets(primaryRecord);
  const rawHints = getSuggestedRecordRawHints(primaryRecord);
  const sourceSpanText = String(primaryRecord.sourceText || refreshedText || updatedEntry.text || '').trim();
  const needsReview = Boolean(
    primaryRecord.needsReview
    || primaryRecord.reviewState === 'needs_review'
    || suggestedRecords.length > 1
  );
  const reviewReasons = [
    ...(Array.isArray(primaryRecord.reviewReasons) ? primaryRecord.reviewReasons : []),
    ...(suggestedRecords.length > 1 ? ['AI found more than one possible timeline entry in this edit.'] : []),
  ];

  return {
    category: refreshedCategory,
    title: record.title || updatedEntry.title || '',
    titlePrefix: LOG_TYPES[refreshedCategory]?.displayLabel || record.title || updatedEntry.title || refreshedCategory,
    description: refreshedText || updatedEntry.text,
    summary: refreshedText || updatedEntry.text,
    details: refreshedText || updatedEntry.text,
    sourceSpanText,
    displayText: refreshedText || updatedEntry.text,
    storyBuckets,
    rawHints,
    suggestedRecord: primaryRecord || null,
    parsedObservation: refreshedCategory !== 'log',
    parsedSourceCategory: refreshedCategory,
    parsedSourceType: refreshedCategory,
    parsedSourceConfidence: primaryRecord.confidence || 'low',
    medicationName: record.medicationName || '',
    medicationDetails: refreshedCategory === 'medication' ? record : null,
    foodDetails: refreshedCategory === 'food' ? record : null,
    activityType: record.activityType || '',
    activityTypes: record.activityType ? [record.activityType] : [],
    severity: record.severity || '',
    triggerSummary: record.trigger || '',
    reviewConfidence: needsReview ? 'review' : (primaryRecord.confidence || 'ready'),
    reviewConfidenceLabel: needsReview ? 'Needs a quick check' : 'Refreshed',
    reviewedVia: null,
    refreshedAt: serverTimestamp(),
    refreshedBy: updatedBy || null,
    refreshState: needsReview ? 'needs_review' : 'refreshed',
    'metadata.needsReview': needsReview,
    'metadata.captureFirstReviewState': needsReview ? 'needs_review' : 'ready_to_save',
    'metadata.reviewConfidence': needsReview ? 'review' : (primaryRecord.confidence || 'ready'),
    'metadata.reviewConfidenceLabel': needsReview ? 'Needs a quick check' : 'Refreshed',
    'metadata.reviewReasons': reviewReasons,
    'metadata.storyBuckets': storyBuckets,
    'metadata.careDetails': Array.isArray(primaryRecord.careDetails) ? primaryRecord.careDetails : [],
    'metadata.rawHints': rawHints,
    'metadata.sourceSpanText': sourceSpanText,
    'metadata.displayText': refreshedText || updatedEntry.text,
    'metadata.captureFirstPromptVersion': extractionResult?.extractor?.promptVersion || '',
    'metadata.captureFirstProvider': extractionResult?.extractor?.provider || '',
    'metadata.captureFirstSourceType': 'timeline_edit',
    'metadata.captureFirstCandidateType': primaryRecord.candidateType || '',
    'metadata.captureFirst.needsReview': needsReview,
    'metadata.captureFirst.reviewLabel': needsReview ? 'Needs a quick check' : 'Refreshed',
    'metadata.captureFirst.status': 'refreshed',
    'metadata.captureFirst.readyToSaveCount': extractionResult?.readyToSaveCount || 0,
    'metadata.captureFirst.needsReviewCount': extractionResult?.needsReviewCount || 0,
    'metadata.captureFirst.promptVersion': extractionResult?.extractor?.promptVersion || null,
    'metadata.captureFirst.provider': extractionResult?.extractor?.provider || null,
    'metadata.captureFirst.understanding': extractionResult?.understanding || null,
    'metadata.timelineEditRefresh': {
      refreshedAt: serverTimestamp(),
      refreshedBy: updatedBy || null,
      suggestedRecordCount: suggestedRecords.length,
      originalTimelineEntryId: updatedEntry.id,
    },
    'metadata.suggestedRecord': primaryRecord || null,
    'source.extractedBy': extractionResult?.extractor?.provider || 'capture_first_backend_ai',
    'source.confidence': primaryRecord.confidence || 'low',
    'source.parsedObservation': refreshedCategory !== 'log',
    'source.parsedSourceCategory': refreshedCategory,
  };
};

export const buildTimelineRefreshProcessingPayload = ({ updatedBy = null } = {}) => ({
  ...buildClearedTimelineDetails(),
  refreshedAt: null,
  refreshedBy: updatedBy || null,
  refreshState: 'refreshing',
  reviewConfidenceLabel: 'Refreshing',
  'metadata.timelineEditRefresh': {
    requestedAt: serverTimestamp(),
    requestedBy: updatedBy || null,
    status: 'refreshing',
  },
  'metadata.captureFirst.status': 'refreshing',
  'metadata.captureFirst.reviewLabel': 'Refreshing',
});

export const buildTimelineRefreshFailedPayload = ({ updatedBy = null, error } = {}) => ({
  ...buildClearedTimelineDetails(),
  refreshState: 'refresh_failed',
  reviewConfidenceLabel: 'Refresh failed',
  'metadata.timelineEditRefresh': {
    failedAt: serverTimestamp(),
    requestedBy: updatedBy || null,
    status: 'failed',
    error: error?.message || 'AI refresh could not finish.',
  },
  'metadata.captureFirst.status': 'refresh_failed',
  'metadata.captureFirst.reviewLabel': 'Refresh failed',
});
